import { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePublicProfile } from '@/hooks/usePublicProfile';
import ProfileHighlights from '@/components/profile/ProfileHighlights';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { EmptyState } from '@/components/ui/EmptyState';
import { useI18n } from '@/contexts/I18nContext';

const UserProfile = () => {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  const loggedUsername = user?.user_metadata?.username;

  const { profile, loading, error } = usePublicProfile(username);

  // Estado da busca anterior (para preservar no retorno)
  const searchState = location.state?.searchState;
  const isFromSearch = !!searchState;
  const isFromHome = !!location.state?.fromHome;

  useEffect(() => {
    if (loggedUsername && username && loggedUsername === username) {
      navigate('/profile', { replace: true });
    }
  }, [loggedUsername, username, navigate]);

  useEffect(() => {
    if (error) {
      navigate('/', { replace: true });
    }
  }, [error, navigate]);

  const handleBack = () => {
    if (searchState) {
      // Retornar para a busca com estado preservado
      navigate('/search', { 
        state: { 
          searchState,
          preserveSearch: true 
        } 
      });
    } else if (isFromHome) {
      // Retornar para a home
      navigate('/home');
    } else {
      // Retornar para a página anterior ou home
      navigate(-1);
    }
  };

  if (loggedUsername && username && loggedUsername === username) {
    // Redirecting...
    return null;
  }

  if (loading) {
    return (
      <>
        <TopNavigation />
        <div className="min-h-screen bg-[#160C0C] pt-20 pb-20">
          <div className="w-full px-4">
            <div className="max-w-2xl mx-auto">
              {/* Botão voltar - apenas se veio da busca ou da home */}
              {(isFromSearch || isFromHome) && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {isFromSearch ? 'Back to Search' : 'Back to Home'}
                  </span>
                </button>
              )}
              
              <div className="text-center">
                <div className="text-white text-lg font-medium">Loading profile...</div>
              </div>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <TopNavigation />
        <div className="min-h-screen bg-[#160C0C] pt-20 pb-20">
          <div className="w-full px-4">
            <div className="max-w-2xl mx-auto">
              {/* Botão voltar - apenas se veio da busca ou da home */}
              {(isFromSearch || isFromHome) && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {isFromSearch ? 'Back to Search' : 'Back to Home'}
                  </span>
                </button>
              )}
              
              <EmptyState
                icon={AlertCircle}
                title={t('profile.not_found')}
                subtitle={t('profile.not_found_description')}
                className="text-white"
              />
            </div>
          </div>
        </div>
        <BottomNavigation />
      </>
    );
  }
  
  if (!profile) {
    return (
      <>
        <TopNavigation />
        <div className="min-h-screen bg-[#160C0C] pt-20 pb-20">
          <div className="w-full px-4">
            <div className="max-w-2xl mx-auto">
              {/* Botão voltar - apenas se veio da busca ou da home */}
              {(isFromSearch || isFromHome) && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {isFromSearch ? 'Back to Search' : 'Back to Home'}
                  </span>
                </button>
              )}
              
              <EmptyState
                icon={AlertCircle}
                title={t('profile.not_found')}
                subtitle={t('profile.not_found_description')}
                className="text-white"
              />
            </div>
          </div>
        </div>
        <BottomNavigation />
      </>
    );
  }

  return (
    <>
      <TopNavigation />
      <div className="min-h-screen w-full bg-[#160C0C] pt-20 pb-20">
        {/* Botão voltar - apenas se veio da busca ou da home */}
        {(isFromSearch || isFromHome) && (
          <div className="px-4 mb-4">
            <div className="max-w-2xl mx-auto">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-white/60 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {isFromSearch ? 'Back to Search' : 'Back to Home'}
                </span>
              </button>
            </div>
          </div>
        )}
        
        <div className="w-full px-4">
          <div className="max-w-2xl mx-auto">
            <ProfileHighlights
              fullName={profile.full_name || ''}
              username={profile.username || ''}
              belt={profile.belt_rank || ''}
              beltColor={profile.belt_color || '#fff'}
              academies={profile.academies || []}
              location={{ country: profile.country, state: profile.state, city: profile.city }}
              social={{ instagram: profile.instagram, x: profile.x, facebook: profile.facebook, youtube: profile.youtube }}
              website={profile.website}
              interests={profile.interests || []}
              profession={profile.profession || ''}
              favoriteTechniques={profile.favorite_techniques || {}}
              techniqueCategories={profile.technique_categories || []}
              businessType={profile.business_type}
              linkedin={profile.linkedin}
              profilePictureUrl={profile.profile_picture_url}
              editable={false}
              isOwnProfile={false}
              userId={profile.id}
              isInstructor={profile.is_instructor || false}
            />
          </div>
        </div>
      </div>
      <BottomNavigation />
    </>
  );
};

export default UserProfile; 